import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import UseGoogleMap from "./UseGoogleMap";
import StoreInfo from "./StoreInfo";
import SearchScreen from "./SearchScreen";
import { useSearchContext, PUBLIC_CATEGORIES, ALL_CATEGORIES } from "../../context/searchContext";
import {
    label_category_sweets, label_category_cafe, label_category_bakery,
} from "../../assets/label/label";

const GENRE_CHIPS = [
    { label: label_category_sweets, icon: '🍰', color: '#c97bab' },
    { label: label_category_cafe, icon: '☕', color: '#8a6e5e' },
    { label: label_category_bakery, icon: '🍞', color: '#b87040' },
];

function FilterBar() {
    const { searchCategory, setSearchCategory, showAll } = useSearchContext();

    const activeGenre = GENRE_CHIPS.find(g => g.label === searchCategory)?.label ?? null;
    const defaultCat = showAll ? ALL_CATEGORIES : PUBLIC_CATEGORIES;

    const handleGenreChip = (label) => {
        setSearchCategory(activeGenre === label ? defaultCat : label);
    };

    return (
        <Box sx={{
            px: { xs: 1.5, md: 2.5 },
            py: '10px',
            borderBottom: '1px solid var(--line)',
            bgcolor: 'var(--paper)',
            display: 'flex',
            alignItems: 'center',
            gap: 0.875,
            overflowX: 'auto',
            flexShrink: 0,
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
        }}>
            {GENRE_CHIPS.map(g => (
                <Box
                    key={g.label}
                    onClick={() => handleGenreChip(g.label)}
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.625,
                        px: '12px', py: '7px',
                        border: '1.5px solid',
                        borderColor: activeGenre === g.label ? g.color : 'var(--line)',
                        bgcolor: activeGenre === g.label ? g.color : 'var(--paper)',
                        color: activeGenre === g.label ? 'white' : 'var(--ink)',
                        borderRadius: '999px',
                        fontSize: 12, fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        transition: 'all 0.15s',
                        userSelect: 'none',
                    }}
                >
                    <span style={{ fontSize: 13 }}>{g.icon}</span>
                    <span>{g.label}</span>
                </Box>
            ))}

        </Box>
    );
}

function MobileToggle({ mode, setMode }) {
    return (
        <Box sx={{
            px: 1.5, py: '8px',
            display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'var(--paper)',
            borderBottom: '1px solid var(--line)',
            flexShrink: 0,
        }}>
            <Box sx={{
                display: 'flex', gap: 0,
                bgcolor: 'var(--surface)',
                borderRadius: '10px',
                p: '3px',
            }}>
                {[
                    { key: 'list', label: '📋 リスト' },
                    { key: 'map', label: '🗺 マップ' },
                ].map(m => (
                    <Box
                        key={m.key}
                        component="button"
                        onClick={() => setMode(m.key)}
                        sx={{
                            px: '14px', py: '6px',
                            border: 'none',
                            bgcolor: mode === m.key ? 'var(--paper)' : 'transparent',
                            color: 'var(--ink)',
                            borderRadius: '8px',
                            fontSize: 12, fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            boxShadow: mode === m.key ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s',
                        }}
                    >
                        {m.label}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function MainContent({ filterOpen, onOpenFilter, onFilterClose }) {
    const [mapMode, setMapMode] = useState('list');
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'));

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'var(--paper)' }}>
            <FilterBar />

            {!isMd && <MobileToggle mode={mapMode} setMode={setMapMode} />}

            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                {isMd ? (
                    <>
                        {/* PC: 左リスト (440px) + 右マップ */}
                        <Box sx={{
                            width: 440,
                            flexShrink: 0,
                            borderRight: '1px solid var(--line)',
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <StoreInfo selectedStoreId={selectedStoreId} />
                            {/* フッター */}
                            <Box sx={{ px: 2, py: 2.5, mt: 'auto', borderTop: '1px solid var(--line)', bgcolor: 'var(--surface-2)' }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--muted)' }}>
                                    © 2026 神戸グルメマップ
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'hidden' }}>
                            <UseGoogleMap height="100%" onPinClick={setSelectedStoreId} />
                        </Box>
                    </>
                ) : (
                    mapMode === 'list' ? (
                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            <StoreInfo />
                            <Box sx={{ px: 2, py: 2, borderTop: '1px solid var(--line)', bgcolor: 'var(--surface-2)' }}>
                                <Typography sx={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
                                    © 2026 神戸グルメマップ
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ flex: 1 }}>
                            <UseGoogleMap height="100%" />
                        </Box>
                    )
                )}
            </Box>

            {filterOpen && (
                <SearchScreen open={filterOpen} onClose={onFilterClose} />
            )}
        </Box>
    );
}

export default MainContent;
